import { MiddlewareAPI, AnyAction } from 'redux';
import {
    LayoutActionType,
    LayoutActionStateChange,
    LayoutStateChangeSubtype,
    LayoutAction,
    layoutSetActiveInputAction,
} from 'layout/store/actions';
import { wasUpdatedFromLayout } from 'layout/store/thunks';
import { ThunkDispatch } from 'store/store';
import State from 'store/State';
import {
    selectLayout,
    selectActiveInputWidgetIdFromLayout,
    selectLayoutComponents,
    selectWidgets,
} from 'layout/store/selectors';
import { AppSettingsActionType } from 'app/store/appSettings/actions';
import { WidgetsService } from 'widgets/WidgetsService';
import { LayoutStatus, Layout } from 'layout/LayoutState';
import { LayoutContainerShape, LayoutComponentShape } from 'layout/LayoutShape';
import { visit } from './reducer';
import Services from 'store/Services';
import { WidgetInfo } from 'widgets/WidgetInfo';
import { portfolioSetActiveInputActionAsync } from 'portfolio/store/thunks';
import without from 'lodash/without';
import { buildLogger } from '@instinet-analytics/core-lib';
import {
    ConfirmAddWidgetsAction,
    LayoutRendererActionType,
} from 'layoutRenderer/store/actions.ts';

export const layoutMiddleware = (services: Services) => [
    layoutSaveMiddleware(services),
    updateActiveInputMiddleware(services),
    updateOrdersStreamOnLayoutLoadMiddleware(services),
    updateOrdersStreamOnOrderConsumerAddedMiddleware(services),
    updateWidgetStreamsOnLayoutChangedMiddleware(services),
    updateDocumentTitleOnLayoutLoadMiddleware(),
    recordWidgetUsageOnLayoutLoadMiddleware(services),
    recordWidgetUsageOnWidgetAdded(services),
];

export function layoutSaveMiddleware(services: Services) {
    const { widgetsService, currentLayoutPersistenceService, loggingService } =
        services;
    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            const prevState = middlewareAPI.getState();

            next(action);

            if (selectLayout(prevState) === undefined) {
                return;
            }

            if (!actionRequiresLayoutSave(action, widgetsService)) {
                return;
            }

            loggingService.debug(
                'store|layoutSaveMiddleware',
                'Action requires layout save',
                action
            );

            currentLayoutPersistenceService.debouncePersist();
        };
}

function actionRequiresLayoutSave(
    action: AnyAction,
    widgetsService: WidgetsService
): boolean {
    if (action.type === LayoutActionType.LAYOUT_STATE_CHANGE) {
        const layoutStateChangeAction = action as LayoutActionStateChange;
        return (
            layoutStateChangeAction.payload!.subType !==
            LayoutStateChangeSubtype.LOAD
        );
    }

    if (wasUpdatedFromLayout(action)) {
        return false;
    }

    if (widgetsService.isWidgetActionAffectingLayout(action)) {
        return true;
    }

    return action.type === AppSettingsActionType.UPDATE;
}

export function updateActiveInputMiddleware(services: Services) {
    const { widgetsService, loggingService, portfolioService } = services;
    const logger = buildLogger(
        'Layout updateActiveInputMiddleware',
        loggingService
    );

    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: LayoutAction) => {
            const prevActiveInputId = selectActiveInputWidgetIdFromLayout(
                middlewareAPI.getState()
            );
            next(action);

            if (action.type !== LayoutActionType.LAYOUT_STATE_CHANGE) {
                return;
            }

            const layoutState = action.payload!.layoutState;
            if (layoutState.status !== LayoutStatus.SUCCESS) {
                return;
            }

            const nextActiveInput = getFirstActiveInputFromLayout(
                action.payload!.layoutState.result!,
                widgetsService
            );

            const prevId = prevActiveInputId || '';
            const nextId = (nextActiveInput && nextActiveInput!.id) || '';

            if (prevId !== nextId) {
                logger.debug('Active input changed', {
                    prev: prevId,
                    next: nextId,
                });
                middlewareAPI.dispatch(layoutSetActiveInputAction(nextId));

                if (!portfolioService.activeInputOverridesWidget) {
                    middlewareAPI.dispatch(
                        portfolioSetActiveInputActionAsync(
                            nextActiveInput ? 'Widget' : 'None',
                            nextActiveInput
                        )
                    );
                }
            }
        };
}

export function updateOrdersStreamOnLayoutLoadMiddleware(services: Services) {
    const { loggingService, ordersStreamingService } = services;

    return () => (next: ThunkDispatch<AnyAction>) => (action: LayoutAction) => {
        // update the state
        next(action);

        if (action.type !== LayoutActionType.LAYOUT_STATE_CHANGE) {
            return;
        }

        const layoutStateChangeAction = action as LayoutActionStateChange;
        const payload = layoutStateChangeAction.payload!;
        if (
            payload.layoutState.status === LayoutStatus.SUCCESS &&
            payload!.subType === LayoutStateChangeSubtype.LOAD
        ) {
            ordersStreamingService.startOrUpdateOrdersStream();

            loggingService.debug(
                'store|updateOrdersStreamOnLayoutLoadMiddleware',
                `Starting/Updating orders stream`,
                action
            );
        }
    };
}

export function recordWidgetUsageOnLayoutLoadMiddleware(services: Services) {
    const { loggingService, clickstreamService } = services;

    return (api: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: LayoutAction) => {
            // update the state
            next(action);

            if (action.type !== LayoutActionType.LAYOUT_STATE_CHANGE) {
                return;
            }

            const layoutStateChangeAction = action as LayoutActionStateChange;
            const payload = layoutStateChangeAction.payload!;
            if (
                payload.layoutState.status === LayoutStatus.SUCCESS &&
                payload!.subType === LayoutStateChangeSubtype.LOAD
            ) {
                const widgets = selectWidgets(api.getState(), true);
                const widgetTypes = widgets.map((w) => w.type);

                loggingService.debug(
                    'store|recordWidgetUsageOnLayoutLoadMiddleware',
                    `Recording widget usage`,
                    { action, widgetTypes }
                );

                clickstreamService.recordWidgetUsages(widgetTypes);
                clickstreamService.flushWidgetUsage();
            }
        };
}

export function recordWidgetUsageOnWidgetAdded(services: Services) {
    const { loggingService, clickstreamService } = services;

    return () =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: ConfirmAddWidgetsAction) => {
            // update the state
            next(action);

            if (action.type !== LayoutRendererActionType.CONFIRM_ADD_WIDGETS) {
                return;
            }

            const addWidgetAction = action as ConfirmAddWidgetsAction;
            const payload = addWidgetAction.payload!;
            const widgetTypes = payload.ids.map((i) => i.widgetType);

            loggingService.debug(
                'store|recordWidgetUsageOnWidgetAdded',
                `Recording widget usage`,
                { action, widgetTypes }
            );

            clickstreamService.recordWidgetUsages(widgetTypes);
        };
}

export function updateDocumentTitleOnLayoutLoadMiddleware() {
    return () => (next: ThunkDispatch<AnyAction>) => (action: LayoutAction) => {
        // update the state
        next(action);

        if (action.type !== LayoutActionType.LAYOUT_STATE_CHANGE) {
            return;
        }

        const layoutStateChangeAction = action as LayoutActionStateChange;
        const payload = layoutStateChangeAction.payload!;
        if (
            payload.layoutState.status === LayoutStatus.SUCCESS &&
            payload!.subType === LayoutStateChangeSubtype.LOAD
        ) {
            document.title = `Instinet Indigo: ${layoutStateChangeAction.payload?.layoutState.result?.name}`;
        }
    };
}

export function updateOrdersStreamOnOrderConsumerAddedMiddleware(
    services: Services
) {
    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            const { loggingService, widgetsService, ordersStreamingService } =
                services;

            const previousState = middlewareAPI.getState();
            const previousComponents = selectLayoutComponents(previousState);

            const previousOrderConsumingWidgets = widgetsService
                .getOrderConsumingWidgets(previousComponents)
                .map((w) => w.id);

            // update the state
            next(action);

            const state = middlewareAPI.getState();
            const components = selectLayoutComponents(state);
            const nextOrderConsumingWidgets = widgetsService
                .getOrderConsumingWidgets(components)
                .map((w) => w.id);
            if (
                without(
                    nextOrderConsumingWidgets,
                    ...previousOrderConsumingWidgets
                ).length > 0
            ) {
                ordersStreamingService.startOrUpdateOrdersStream();

                loggingService.debug(
                    'store|updateOrdersStreamOnOrderConsumerAddedMiddleware',
                    `Starting/Updating orders stream`,
                    action
                );
            }
        };
}

export function updateWidgetStreamsOnLayoutChangedMiddleware(
    services: Services
) {
    const { widgetStreamingService } = services;

    return () => (next: ThunkDispatch<AnyAction>) => (action: LayoutAction) => {
        next(action);

        if (action.type !== LayoutActionType.LAYOUT_STATE_CHANGE) {
            return;
        }

        widgetStreamingService.checkAllStreams('Layout Change');
    };
}

function getFirstActiveInputFromLayout(
    layout: Layout,
    widgetsService: WidgetsService
): WidgetInfo | undefined {
    let activeInput: WidgetInfo | undefined;
    visit(layout && layout.shape, (c) => {
        if (c.type === 'stack') {
            const stack = c as LayoutContainerShape;
            const componentAtIndex = c.content[
                stack.activeItemIndex! || 0
            ] as LayoutComponentShape;
            if (widgetsService.isInput(componentAtIndex.componentType)) {
                if (!activeInput) {
                    activeInput = {
                        id: componentAtIndex.componentId,
                        type: componentAtIndex.componentType,
                    };
                }
            }
        }
    });
    return activeInput;
}
